import type { KeyboardEvent, ReactNode } from 'react';
import { useEffect, useReducer, useRef } from 'react';
import styled, { css } from 'styled-components';
import { focusStyles } from '@elfsight/widget-ui';
import Draggable from 'react-draggable';
import { useSwipeable } from 'react-swipeable';

const Container = styled.div<{
  _elementWidth: string;
  _elementHeight: string;
}>`
  position: absolute;
  overflow: hidden;
  z-index: 2;
  transform-style: preserve-3d;
  backface-visibility: hidden;

  ${({ _elementWidth: width, _elementHeight: height }) =>
    css({
      width,
      height
    })};
`;

const Carriage = styled.div<{
  _elementWidth: string;
  _elementHeight: string;
  _smoothTransition: boolean;
  _withTransitionDelay: boolean;
}>`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;

  ${({ _smoothTransition }) =>
    _smoothTransition && 'transition: transform 0.4s ease;'};
  ${({ _smoothTransition, _withTransitionDelay }) =>
    _smoothTransition && _withTransitionDelay && 'transition-delay: 0.014s;'};

  ${({ _elementWidth: width, _elementHeight: height }) =>
    css({
      width,
      height
    })};

  &:focus-visible > * {
    ${focusStyles}
  }
`;

const initialState = {
  isDragging: false,
  isSimulatedDragging: false,
  isDragMoving: false
};

const reducer = (state = initialState, action) => {
  const { type: actionType } = action;

  switch (actionType) {
    default:
      throw new Error(`Unknown action type "${actionType}"`);

    case 'START_DRAG':
      return {
        ...state,
        isDragging: true
      };

    case 'STOP_DRAG':
      return {
        ...state,
        isDragging: false
      };

    case 'START_SIMULATED_DRAG':
      return {
        ...state,
        isSimulatedDragging: true
      };

    case 'STOP_SIMULATED_DRAG':
      return {
        ...state,
        isSimulatedDragging: false
      };

    case 'START_DRAG_MOVE':
      return {
        ...state,
        isDragMoving: true
      };

    case 'STOP_DRAG_MOVE':
      return {
        ...state,
        isDragMoving: false
      };
  }
};

interface PairSliderDivisionControlProps {
  width: number;
  height: number;
  axis: 'x' | 'y';
  initialValue: number;
  value: number;
  simulateDragOnHover: boolean;
  resetOnPointerOut: boolean;
  smoothTransition: boolean;
  isSafari: boolean;
  onChange: (value: number) => void;
  onUserInteractionStart: () => void;
  onUserInteractionEnd: () => void;
  onDragMoveStart: () => void;
  onDragMoveEnd: () => void;
  children?: ReactNode;
}

export function PairSliderDivisionControl({
  width,
  height,
  axis,
  initialValue,
  value,
  simulateDragOnHover,
  resetOnPointerOut,
  smoothTransition,
  isSafari,
  onChange,
  onUserInteractionStart,
  onUserInteractionEnd,
  onDragMoveStart,
  onDragMoveEnd,
  children
}: PairSliderDivisionControlProps) {
  const isAxisX = axis === 'x';
  const isAxisY = axis === 'y';

  // determine draggable carriage movement bounds and current position
  let dragBounds = null;
  let dragPosition = null;

  if (isAxisX) {
    dragBounds = {
      left: -width,
      right: 0
    };

    dragPosition = {
      x: value * width - width,
      y: 0
    };
  } else {
    dragBounds = {
      top: -height,
      bottom: 0
    };

    dragPosition = {
      x: 0,
      y: value * height - height
    };
  }

  const containerRef = useRef();
  const carriageRef = useRef();

  const [state, dispatch] = useReducer(reducer, initialState);
  const { isDragging, isSimulatedDragging, isDragMoving } = state;

  const startDrag = () => {
    if (!isDragging) {
      dispatch({ type: 'START_DRAG' });
    }
  };

  const stopDrag = () => {
    if (isDragging) {
      dispatch({ type: 'STOP_DRAG' });
    }
  };

  const startSimulatedDrag = () => {
    if (!isSimulatedDragging) {
      dispatch({ type: 'START_SIMULATED_DRAG' });
    }
  };

  const stopSimulatedDrag = () => {
    if (isSimulatedDragging) {
      dispatch({ type: 'STOP_SIMULATED_DRAG' });
    }
  };

  const startDragMove = () => {
    if (!isDragMoving) {
      dispatch({ type: 'START_DRAG_MOVE' });
    }
  };

  const stopDragMove = () => {
    if (isDragMoving) {
      dispatch({ type: 'STOP_DRAG_MOVE' });
    }
  };

  // calculates relative offset of a carriage (0 - 1.0) by real (px) value
  const getRelativeValue = (realValue) =>
    1 + realValue / (isAxisX ? width : height);

  // returns relative coordinates of a point (clientX, clientY) which are bounded to boundingElement
  const getRelativeCoordinates = (boundingElement, clientX, clientY) => {
    const { left: offsetX, top: offsetY } =
      boundingElement.getBoundingClientRect();

    return {
      x: clientX - offsetX,
      y: clientY - offsetY
    };
  };

  // dispatches onChange event listener using a value calculated
  // by pointer event absolute coordinates (clientX, clientY)
  const dispatchChangeByPointerCoordinates = (clientX, clientY) => {
    const { current } = containerRef;

    if (current) {
      const { x, y } = getRelativeCoordinates(current, clientX, clientY);
      onChange(getRelativeValue(isAxisY ? y - height : x - width));
    }
  };

  const handleSwiped = (eventData) => {
    const { absX, absY, dir, velocity } = eventData;
    const time = Math.sqrt(absX ** 2 + absY ** 2) / velocity;

    if (velocity > 0.6 && time <= 1000) {
      if ((isAxisX && dir === 'Left') || (isAxisY && dir === 'Up')) {
        onChange(0);
      } else if ((isAxisX && dir === 'Right') || (isAxisY && dir === 'Down')) {
        onChange(1);
      }
    }
  };

  const swipeHandlers = useSwipeable({
    onSwiped: handleSwiped,
    trackMouse: true,
    preventScrollOnSwipe: true
  });

  const handleContainerPointerDown = (event) => {
    const { target: targetElement } = event;
    const { current: carriageElement } = carriageRef;

    if (targetElement === carriageElement) {
      const { pointerType, button } = event;

      if (pointerType === 'mouse' && button === 0) {
        const { clientX, clientY } = event;
        dispatchChangeByPointerCoordinates(clientX, clientY);
      }
    }
  };

  const handleContainerPointerLeave = () => {
    if (simulateDragOnHover) {
      stopSimulatedDrag();
    }

    if (resetOnPointerOut && onChange) {
      requestAnimationFrame(() => onChange(initialValue));
    }
  };

  const handleDraggableDrag = (event, data) => {
    startDragMove();

    if (onChange) {
      onChange(getRelativeValue(data[axis]));
    }
  };

  const handleDraggableStop = () => {
    stopDrag();
    stopDragMove();
  };

  useEffect(() => {
    onChange(initialValue);
  }, [initialValue, axis]);

  useEffect(() => {
    if (onUserInteractionStart && (isDragging || isSimulatedDragging)) {
      onUserInteractionStart();
    } else if (onUserInteractionEnd) {
      onUserInteractionEnd();
    }
  }, [isDragging, isSimulatedDragging]);

  useEffect(() => {
    if (onDragMoveStart && isDragMoving) {
      onDragMoveStart();
    } else if (onDragMoveEnd) {
      onDragMoveEnd();
    }
  }, [isDragMoving]);

  useEffect(() => {
    if (simulateDragOnHover && isSimulatedDragging && !isDragging) {
      const options = {
        capture: false,
        passive: true
      };

      const listener = ({ clientX, clientY }) => {
        dispatchChangeByPointerCoordinates(clientX, clientY);
      };

      window.addEventListener('mousemove', listener, options);

      return () => {
        window.removeEventListener('mousemove', listener, options);
      };
    }

    return undefined;
  }, [simulateDragOnHover, isSimulatedDragging, isDragging]);

  const handleKeyDown = (e: KeyboardEvent) => {
    const step = 0.02;
    let newValue = value;
    let handled = false;

    if (isAxisX) {
      if (e.key === 'ArrowLeft') {
        newValue = Math.max(0, newValue - step);
        handled = true;
      }

      if (e.key === 'ArrowRight') {
        newValue = Math.min(1, newValue + step);
        handled = true;
      }
    }

    if (isAxisY) {
      if (e.key === 'ArrowUp') {
        newValue = Math.max(0, newValue - step);
        handled = true;
      }

      if (e.key === 'ArrowDown') {
        newValue = Math.min(1, newValue + step);
        handled = true;
      }
    }

    if (handled) {
      e.preventDefault();
      e.stopPropagation();
      onChange(newValue);
    }
  };

  return (
    <div {...swipeHandlers}>
      <Container
        ref={containerRef}
        onPointerDown={handleContainerPointerDown}
        onPointerEnter={simulateDragOnHover ? startSimulatedDrag : null}
        onPointerLeave={handleContainerPointerLeave}
        _elementWidth={`${width}px`}
        _elementHeight={`${height}px`}
      >
        <Draggable
          axis={axis}
          bounds={dragBounds}
          position={dragPosition}
          defaultClassName=""
          defaultClassNameDragging=""
          defaultClassNameDragged=""
          onDrag={handleDraggableDrag}
          onStart={startDrag}
          onStop={handleDraggableStop}
        >
          <Carriage
            ref={carriageRef}
            _elementWidth={`${isAxisX ? width * 2 : width}px`}
            _elementHeight={`${isAxisX ? height : height * 2}px`}
            _smoothTransition={smoothTransition}
            _withTransitionDelay={isSafari}
            tabIndex={0}
            role="slider"
            aria-orientation={isAxisX ? 'horizontal' : 'vertical'}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(value * 100)}
            aria-valuetext={`${Math.round(value * 100)} %`}
            aria-label="Comparison slider separator"
            onKeyDown={handleKeyDown}
          >
            {children}
          </Carriage>
        </Draggable>
      </Container>
    </div>
  );
}
